import React from "react";

import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IMaskInput } from "react-imask";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, inputClasses, wrapperClasses } from "./styles";

import type { JSX } from "react";

import type { InputProps } from "./types";

function TextInputWithMask({
  name,
  icon,
  isReadOnly,
  isCompact,
  className,
  isNoWrap,
  label,
  sublabel,
  isRequired,
  isTooltipErrorMessage,
  validate,
  mask,
  blocks,
  type,
  onBlurCustom,
  placeholder,
}: InputProps): JSX.Element {
  const { t } = useTranslation("editor-errors");
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }): JSX.Element => {
        const { onChange, onBlur, ref, value } = field;
        const { invalid: isInvalid, error } = fieldState;
        return (
          <FieldContainer
            className={className}
            isCompact={isCompact}
            isNoWrap={isNoWrap}
          >
            {label ? (
              <Label
                isCompact={isCompact}
                isRequired={isRequired}
                label={label}
                name={name}
                sublabel={sublabel}
              />
            ) : null}
            <div className={wrapperClasses({ isCompact })}>
              <div className={containerClasses()}>
                <div className="text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[0.46875rem]">
                  {icon}
                </div>
                <IMaskInput
                  blocks={blocks}
                  className={inputClasses({ icon, isInvalid, isReadOnly })}
                  data-tooltip-content={error?.message}
                  data-tooltip-id={`field-${name}-tooltip`}
                  disabled={isReadOnly}
                  inputRef={ref}
                  mask={mask}
                  onAccept={(currentValue): void => onChange(currentValue)}
                  placeholder={placeholder}
                  type={type}
                  value={value}
                  onBlur={(e): void => {
                    onBlur();
                    onBlurCustom?.(e);
                  }}
                />
              </div>
              <ErrorMessage
                error={error}
                isCompact={isCompact}
                isTooltip={isTooltipErrorMessage}
                name={name}
              />
            </div>
          </FieldContainer>
        );
      }}
      rules={{
        required: isRequired && t("requiredField"),
        validate,
      }}
    />
  );
}

export default TextInputWithMask;
